import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiUser, FiShield, FiCheck, FiX, FiRefreshCw, FiUsers, FiPlus, FiUserPlus, FiUserMinus, FiTrash2 } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Admin.css';

const Admin = ({ onBack }) => {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [enterpriseAccounts, setEnterpriseAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Enterprise modal state
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseModalType, setEnterpriseModalType] = useState('create'); // 'create', 'add', 'remove'
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [enterpriseAccountName, setEnterpriseAccountName] = useState('');
  const [enterpriseMemberCount, setEnterpriseMemberCount] = useState(5);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const fetchUsers = useCallback(async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await fetch(getApiEndpoint(`/api/admin/users${params}`), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required');
        }
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/admin/subscription-plans'), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  }, []);

  const fetchEnterpriseAccounts = useCallback(async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/admin/enterprise-accounts'), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setEnterpriseAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to fetch enterprise accounts:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchPlans(), fetchEnterpriseAccounts()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchPlans, fetchEnterpriseAccounts]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchTerm);
  };

  const handleSetSubscription = async (userEmail, planSlug) => {
    setActionLoading(userEmail);
    try {
      const response = await fetch(getApiEndpoint('/api/admin/set-subscription'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          user_email: userEmail,
          plan_slug: planSlug
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update subscription');
      }

      await fetchUsers(searchTerm);
      alert(`Successfully set ${userEmail} to ${planSlug} plan`);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (userEmail, currentIsAdmin) => {
    setActionLoading(userEmail);
    try {
      const response = await fetch(getApiEndpoint('/api/admin/set-admin'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          user_email: userEmail,
          is_admin: !currentIsAdmin
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update admin status');
      }

      await fetchUsers(searchTerm);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const openEnterpriseModal = (type, userEmail = '') => {
    setEnterpriseModalType(type);
    setSelectedUserEmail(userEmail);
    setEnterpriseAccountName('');
    setEnterpriseMemberCount(5);
    setSelectedAccountId('');
    setShowEnterpriseModal(true);
  };

  const handleEnterpriseAction = async () => {
    setActionLoading('enterprise');
    try {
      let endpoint = '';
      let body = {};

      if (enterpriseModalType === 'create') {
        endpoint = '/api/admin/create-enterprise';
        body = {
          user_email: selectedUserEmail,
          account_name: enterpriseAccountName,
          member_count: parseInt(enterpriseMemberCount)
        };
      } else if (enterpriseModalType === 'add') {
        endpoint = '/api/admin/add-to-enterprise';
        body = {
          user_email: selectedUserEmail,
          account_id: parseInt(selectedAccountId)
        };
      } else if (enterpriseModalType === 'remove') {
        endpoint = '/api/admin/remove-from-enterprise';
        body = {
          user_email: selectedUserEmail,
          account_id: parseInt(selectedAccountId)
        };
      }

      const response = await fetch(getApiEndpoint(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Operation failed');
      }

      alert(data.message);
      setShowEnterpriseModal(false);
      await Promise.all([fetchUsers(searchTerm), fetchEnterpriseAccounts()]);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1><FiShield /> Admin Panel</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1><FiShield /> Admin Panel</h1>
          {onBack && <button className="back-button" onClick={onBack}>Back</button>}
        </div>
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1><FiShield /> Admin Panel</h1>
        {onBack && <button className="back-button" onClick={onBack}>Back</button>}
      </div>

      <div className="admin-content">
        {/* Search Section */}
        <div className="admin-section">
          <h2>User Management</h2>
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by email or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <button type="submit" className="search-button">Search</button>
            <button
              type="button"
              className="refresh-button"
              onClick={() => fetchUsers(searchTerm)}
              title="Refresh"
            >
              <FiRefreshCw />
            </button>
          </form>
        </div>

        {/* Users Table */}
        <div className="admin-section">
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Admin</th>
                  <th>Subscription</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-results">
                      No users found. Try a different search term.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        <div className="user-cell">
                          <FiUser className="user-icon" />
                          {user.username}
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`admin-badge ${user.is_admin ? 'is-admin' : ''}`}>
                          {user.is_admin ? <FiCheck /> : <FiX />}
                          {user.is_admin ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <span className={`plan-badge ${user.subscription?.plan_slug || 'free'}`}>
                          {user.subscription?.plan_name || 'Free'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          {/* Subscription dropdown */}
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleSetSubscription(user.email, e.target.value);
                              }
                            }}
                            disabled={actionLoading === user.email}
                            className="plan-select"
                          >
                            <option value="">Set Plan...</option>
                            {plans
                              .filter(p => p.plan_type === 'individual')
                              .map(plan => (
                                <option key={plan.id} value={plan.slug}>
                                  {plan.name}
                                </option>
                              ))
                            }
                          </select>

                          {/* Enterprise dropdown */}
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                openEnterpriseModal(e.target.value, user.email);
                              }
                            }}
                            disabled={actionLoading === user.email}
                            className="plan-select enterprise-select"
                          >
                            <option value="">Enterprise...</option>
                            <option value="create">Create New Account</option>
                            <option value="add">Add to Account</option>
                            <option value="remove">Remove from Account</option>
                          </select>

                          {/* Toggle admin button */}
                          <button
                            onClick={() => handleToggleAdmin(user.email, user.is_admin)}
                            disabled={actionLoading === user.email}
                            className={`admin-toggle-button ${user.is_admin ? 'revoke' : 'grant'}`}
                            title={user.is_admin ? 'Revoke Admin' : 'Grant Admin'}
                          >
                            <FiShield />
                            {user.is_admin ? 'Revoke' : 'Admin'}
                          </button>

                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete user ${user.username} (${user.email})? This permanently removes all their data.`)) return;
                              try {
                                const res = await fetch(getApiEndpoint(`/api/admin/users/${user.id}`), {
                                  method: 'DELETE', headers: getAuthHeaders(),
                                });
                                if (res.ok) {
                                  setUsers(prev => prev.filter(u => u.id !== user.id));
                                } else {
                                  const err = await res.json().catch(() => ({}));
                                  alert(err.detail || 'Delete failed');
                                }
                              } catch (_) { alert('Delete failed'); }
                            }}
                            disabled={actionLoading === user.email}
                            className="admin-toggle-button revoke"
                            title="Delete User"
                          >
                            <FiTrash2 />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enterprise Accounts Section */}
        <div className="admin-section">
          <h2><FiUsers /> Enterprise Accounts</h2>
          {enterpriseAccounts.length === 0 ? (
            <p className="no-data">No enterprise accounts yet.</p>
          ) : (
            <div className="enterprise-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th>Members</th>
                  </tr>
                </thead>
                <tbody>
                  {enterpriseAccounts.map((acc) => (
                    <tr key={acc.id}>
                      <td>{acc.id}</td>
                      <td><strong>{acc.name}</strong></td>
                      <td>{acc.owner_email}</td>
                      <td>
                        <span className={`status-badge ${acc.status}`}>
                          {acc.status}
                        </span>
                      </td>
                      <td>{acc.current_member_count} / {acc.purchased_member_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Plans Reference */}
        <div className="admin-section">
          <h2>Available Plans</h2>
          <div className="plans-grid">
            {plans.map(plan => (
              <div key={plan.id} className="plan-card">
                <h3>{plan.name}</h3>
                <p className="plan-slug">Slug: <code>{plan.slug}</code></p>
                <p className="plan-type">Type: {plan.plan_type}</p>
                <p className="plan-price">
                  ${(plan.price_monthly / 100).toFixed(2)}/mo
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enterprise Modal */}
      {showEnterpriseModal && (
        <div className="modal-overlay" onClick={() => setShowEnterpriseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              {enterpriseModalType === 'create' && <><FiPlus /> Create Enterprise Account</>}
              {enterpriseModalType === 'add' && <><FiUserPlus /> Add to Enterprise</>}
              {enterpriseModalType === 'remove' && <><FiUserMinus /> Remove from Enterprise</>}
            </h2>

            <div className="modal-form">
              <div className="form-group">
                <label>User Email</label>
                <input
                  type="text"
                  value={selectedUserEmail}
                  onChange={(e) => setSelectedUserEmail(e.target.value)}
                  placeholder="user@example.com"
                />
              </div>

              {enterpriseModalType === 'create' && (
                <>
                  <div className="form-group">
                    <label>Account Name</label>
                    <input
                      type="text"
                      value={enterpriseAccountName}
                      onChange={(e) => setEnterpriseAccountName(e.target.value)}
                      placeholder="Company Name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Number of Seats</label>
                    <input
                      type="number"
                      min="1"
                      value={enterpriseMemberCount}
                      onChange={(e) => setEnterpriseMemberCount(e.target.value)}
                    />
                  </div>
                </>
              )}

              {(enterpriseModalType === 'add' || enterpriseModalType === 'remove') && (
                <div className="form-group">
                  <label>Enterprise Account</label>
                  <select
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                  >
                    <option value="">Select account...</option>
                    {enterpriseAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name} ({acc.current_member_count}/{acc.purchased_member_count} members)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setShowEnterpriseModal(false)}
              >
                Cancel
              </button>
              <button
                className={`confirm-button ${enterpriseModalType === 'remove' ? 'danger' : ''}`}
                onClick={handleEnterpriseAction}
                disabled={actionLoading === 'enterprise'}
              >
                {actionLoading === 'enterprise' ? 'Processing...' : (
                  enterpriseModalType === 'create' ? 'Create Account' :
                  enterpriseModalType === 'add' ? 'Add Member' : 'Remove Member'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
